import React, { useState, useEffect, useMemo } from "react";
import MainServices from "../../../../services/MainServices";
import { notifyWarn } from "../../../../assets/elements/Toast";
import { toast } from "react-toastify";
import Edit from "../../../../assets/images/icons/editIconNew.svg";
import { Field, FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import "./personalNew.css";
import CustomErrorMessage from "../../../../assets/common/CustomeErrorMessage";
import HijriDatePicker from "../employeesModals/HijriDatePicker";
import AsyncSelect from "react-select/async";
import ButtonLoading from "../../../../assets/loaders/ButtonLoading";
import dateIcon from "../../../../assets/images/dateIcon.svg";
import moment from "moment";
import { toGregorian, toHijri } from "hijri-converter";
import useRTLContext from "../../../../hooks/useRTLContext";
import { useTranslation } from "react-i18next";

const PersonalNew = ({
  personalInfo,
  handleGetTableData,
  empPublicId,
  getEmployeeRegistrationData,
  currentEmployeeEditing = false,
}) => {
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [religionOptions, setReligionOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const noEditingMsg = "Read-only";

  const {
    getReligionMaster,
    getCountryMaster,
    updatePersonalInfo,
    deactivateEmployee,
  } = MainServices();

  useEffect(() => {
    getReligionOptions();
    getNationalityOptions();
  }, []);

  console.log("personal info: ", personalInfo);

  const getReligionOptions = async () => {
    try {
      const response = await getReligionMaster();
      console.log("religion options: ", response);
      if (response?.data?.data?.doc?.length >= 0) {
        setReligionOptions(
          response?.data?.data?.doc?.map((religion) => ({
            label: religion.name,
            value: religion.publicId,
          }))
        );
      } else {
        setReligionOptions([{ label: "Data not loaded", value: "" }]);
      }
    } catch (error) {
      console.log(error);
      setReligionOptions([{ label: "Data not loaded", value: "" }]);
    }
  };

  const loadReligionOptions = (inputValue, callback) => {
    if (!inputValue) {
      callback(religionOptions);
    } else {
      const filteredOptions = religionOptions.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      callback(filteredOptions);
    }
  };

  const getNationalityOptions = async () => {
    try {
      const response = await getCountryMaster({});
      console.log("country options: ", response);
      if (response?.data && response.data?.data?.doc?.length >= 0) {
        setCountryOptions(
          response?.data?.data?.doc?.map((country) => ({
            label: country.name,
            value: country.publicId,
          }))
        );
      } else {
        setCountryOptions([{ label: "Data not loaded", value: "" }]);
      }
    } catch (error) {
      console.log(error);
      setCountryOptions([{ label: "Data not loaded", value: "" }]);
    }
  };

  const loadOptions = (inputValue, callback) => {
    if (!inputValue) {
      callback(countryOptions);
    } else {
      const filteredOptions = countryOptions.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      callback(filteredOptions);
    }
  };

  const isAtLeast18 = (gregorianDateStr) => {
    const birthDate = moment(gregorianDateStr, "YYYY-MM-DD");
    const age = moment().diff(birthDate, "years");
    return age >= 18;
  };

  const nameRegexRequired = /^[A-Za-z\s'.-]+$/; // required fields
  const nameRegexOptional = /^[A-Za-z\s'.-]*$/; // optional fields

  const getValidationSchema = () => {
    return Yup.object().shape({
      firstName: Yup.string()
        .required("First name is required")
        .matches(
          nameRegexRequired,
          "Only English letters, spaces, apostrophes (’), dashes (-), and periods (.) are allowed."
        )
        .max(30, "First name must not exceed 30 characters"),
      firstNameArb: Yup.string()
        .matches(
          /^[\u0600-\u06FF\s]*$/,
          "First name in Arabic must contain only Arabic letters and no numbers or special characters"
        )
        .max(30, "First name in Arabic must not exceed 30 characters"),
      lastName: Yup.string()
        .required("Last name is required")
        .matches(
          /^[\u0600-\u06FFA-Za-z\s]+$/, // Allows Arabic, English characters, and spaces
          "Last name must not contain special characters or numbers"
        )
        .max(30, "Last name must not exceed 30 characters"),

      lastName: Yup.string()
        .required("Last name is required")
        .matches(
          nameRegexOptional,
          "Only English letters, spaces, apostrophes (’), dashes (-), and periods (.) are allowed."
        )
        .max(30, "Last name must not exceed 30 characters"),

      lastNameArb: Yup.string()
        .required("Last name is required")
        .matches(
          /^[\u0600-\u06FF\s]*$/,
          "Last name in Arabic must contain only Arabic letters and no numbers or special characters"
        )
        .max(30, "Last name in Arabic must not exceed 30 characters"),

      phone: Yup.string()
        .required("Phone number is required")
        .transform((value) => {
          // Convert Arabic numerals to Western numerals
          if (value) {
            return value.replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d));
          }
          return value;
        })
        .matches(
          /^(00966|966|\+966|0)?5\d{8}$/,
          "Enter a valid Saudi mobile number"
        ),

      personalEmail: Yup.string()
        .email("Invalid email format")
        .required("Personal Email is required")
        .trim(),

      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),

      dobHijri: Yup.string()
        .required("Date of birth is required")
        .test(
          "is-18-hijri",
          "User must be at least 18 years old",
          function (value) {
            if (!value) return false;

            let hijriDate;

            // Handle ISO-like format from backend: "1420-09-24T00:00:00.000Z"
            if (value.includes("T")) {
              const datePart = value.split("T")[0]; // "1420-09-24"
              const [y, m, d] = datePart.split("-").map(Number);
              hijriDate = { day: d, month: m, year: y };
            } else {
              // Handle "24-09-1420" (DD-MM-YYYY)
              const [d, m, y] = value.split("-").map(Number);
              hijriDate = { day: d, month: m, year: y };
            }

            if (
              !hijriDate.day ||
              !hijriDate.month ||
              !hijriDate.year ||
              hijriDate.month > 12 ||
              hijriDate.day > 30
            ) {
              return false;
            }

            const gregorian = toGregorian(
              hijriDate.year,
              hijriDate.month,
              hijriDate.day
            );
            const gregorianDateStr = `${gregorian.gy}-${String(
              gregorian.gm
            ).padStart(2, "0")}-${String(gregorian.gd).padStart(2, "0")}`;

            return isAtLeast18(gregorianDateStr);
          }
        ),

      dobGregorian: Yup.string()
        .required("Date of birth is required")
        .test(
          "is-18-gregorian",
          "User must be at least 18 years old",
          function (value) {
            if (!value) return false;

            let formattedValue = value;

            // Handle ISO-like string from backend
            if (value.includes("T")) {
              formattedValue = value.split("T")[0]; // Get "YYYY-MM-DD"
            }

            return isAtLeast18(formattedValue);
          }
        ),

      religion: Yup.object().required("Religion is required"),
      maritalStatus: Yup.string().required("Marital status is required"),
      gender: Yup.string().required("Gender is required"),
      citizen: Yup.string().required("Citizen status is required"),
      nationality: Yup.object().when("citizen", {
        is: (value) => value === "false", // Ensure this matches your form data type
        then: (schema) => schema.required("Nationality is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      nationalId: Yup.string().when("citizen", {
        is: (value) => value === "true",
        then: (schema) => schema.required("National ID is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    });
  };

  const formatDateToDotFormat = (dateString) => {
    if (!dateString) return ""; // Handle empty or invalid input

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return ""; // Handle empty or undefined values

    const date = new Date(dateString);

    // Convert to YYYY-MM-DD format
    return date.toISOString().split("T")[0];
  };

  const asyncSelectStyles = {
    control: (provided) => ({
      ...provided,
      width: "100%",
      height: "56px",
      padding: "0 6px",
      borderRadius: "8px",
      display: "flex",
      justifyContent: "space-between",
      background: "#f9fafb",
      border: "1px solid #eaecf0",
      fontFamily: "Poppins, sans-serif",
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "18px",
      textAlign: "left",
      color: "#111827",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#A0A0A0",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#04080b",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "8px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    }),
    option: (provided, state) => ({
      ...provided,
      background: state.isFocused ? "#f3f4f6" : "#fff",
      color: "#04080b",
      padding: "10px 16px",
      cursor: "pointer",
      fontSize: "12px",
      lineHeight: "18px",
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "200px",
      overflowY: "auto",
    }),
  };

  const convertDateForPayload = (dateString) => {
    if (!dateString || typeof dateString !== "string") {
      return ""; // Return empty string for invalid input
    }

    // Check if the input is in ISO format (e.g., "2000-01-01T00:00:00.000Z")
    if (dateString.includes("T")) {
      return dateString; // Return as is for ISO format
    }

    // Otherwise, assume the input is in "DD-MM-YYYY" format
    const [day, month, year] = dateString.split("-");
    return `${year}-${month}-${day}`;
  };

  const convertAndFormatHijriDate = (dateString) => {
    // If the input is empty or invalid, return an empty string or a default value
    if (!dateString || typeof dateString !== "string") {
      return ""; // or return a default value like "MM-DD-YYYY"
    }

    // Check if the input is in ISO format (e.g., "1446-03-09T00:00:00.000Z")
    if (dateString.includes("T")) {
      // Extract the date part (YYYY-MM-DD) from the ISO string
      const datePart = dateString.split("T")[0];
      const [year, month, day] = datePart.split("-");

      // Validate year, month, and day
      if (!year || !month || !day) {
        return ""; // or return a default value like "MM-DD-YYYY"
      }

      // Format as "MM-DD-YYYY"
      return `${month}-${day}-${year}`;
    } else {
      // Check if the input is already in "MM-DD-YYYY" format
      const dateParts = dateString.split("-");
      if (
        dateParts.length === 3 &&
        dateParts[0].length === 2 &&
        dateParts[1].length === 2 &&
        dateParts[2].length === 4
      ) {
        return dateString; // Return as is if already in "MM-DD-YYYY" format
      } else {
        return ""; // or return a default value like "MM-DD-YYYY"
      }
    }
  };

  const convertHijriToGregorian = (hijriDate, setFieldValue) => {
    // Split the input Hijri date into day, month, and year
    const [day, month, year] = hijriDate.split("-").map(Number);

    // Debug: Log the split values
    console.log("Day:", day, "Month:", month, "Year:", year);

    // Validate the Hijri date inputs
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      console.error(
        "Invalid Hijri date format. Please provide a valid date in the format DD-MM-YYYY."
      );
      return null;
    }

    // Validate the Hijri year, month, and day
    if (year < 1 || month < 1 || month > 12 || day < 1 || day > 30) {
      console.error(
        "Invalid Hijri date. Please provide a valid date within the correct range."
      );
      return null; // Do not set the value if the date is invalid
    }

    // Convert Hijri to Gregorian
    const gregorianDate = toGregorian(year, month, day);

    // Debug: Log the output of toGregorian
    console.log("Gregorian Date Object:", gregorianDate);

    // Check if the conversion was successful and the Gregorian date is valid
    if (
      !gregorianDate ||
      isNaN(gregorianDate.gy) ||
      isNaN(gregorianDate.gm) ||
      isNaN(gregorianDate.gd) ||
      gregorianDate.gy < 1 || // Year must be positive
      gregorianDate.gm < 1 ||
      gregorianDate.gm > 12 || // Month must be between 1 and 12
      gregorianDate.gd < 1 ||
      gregorianDate.gd > 31 // Day must be between 1 and 31
    ) {
      console.error("Conversion failed. Invalid Gregorian date object.");
      setFieldValue("dobGregorian", "");
      return null; // Do not set the value if conversion fails
    }

    // Format the Gregorian date as YYYY-MM-DD
    const formattedGregorianDate = `${gregorianDate.gy}-${String(
      gregorianDate.gm
    ).padStart(2, "0")}-${String(gregorianDate.gd).padStart(2, "0")}`;

    // Optionally, update a field dynamically (if needed)
    if (setFieldValue) {
      setFieldValue("dobGregorian", formattedGregorianDate);
    }

    return formattedGregorianDate;
  };

  const convertGregorianToHijri = (gregorianDate, setFieldValue) => {
    // Split the input Gregorian date into year, month, and day
    const [year, month, day] = gregorianDate.split("-").map(Number);

    // Validate the Gregorian date inputs
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      console.error(
        "Invalid Gregorian date format. Please provide a valid date in the format YYYY-MM-DD."
      );
      return null;
    }

    // Convert Gregorian to Hijri
    const hijriDate = toHijri(year, month, day);

    // Check if any part of the Hijri date is NaN
    if (isNaN(hijriDate.hy) || isNaN(hijriDate.hm) || isNaN(hijriDate.hd)) {
      console.error("Invalid Hijri date conversion. Skipping value update.");
      setFieldValue("dobHijri", "");
      return null; // Do not set the value if conversion fails
    }

    // Format the Hijri date as DD-MM-YYYY
    const formattedHijriDate = `${String(hijriDate.hd).padStart(
      2,
      "0"
    )}-${String(hijriDate.hm).padStart(2, "0")}-${hijriDate.hy}`;

    // Optionally, update a field dynamically (if needed)
    if (setFieldValue) {
      setFieldValue("dobHijri", formattedHijriDate);
    }

    return formattedHijriDate;
  };

  const formatDateToYYYYMMDD = (date) => {
    if (!date) return "";

    const isISODate = /^\d{4}-\d{2}-\d{2}T/.test(date);
    const isDDMMYYYY = /^\d{2}\/\d{2}\/\d{4}$/.test(date);
    const isYYYYMMDD = /^\d{4}-\d{2}-\d{2}$/.test(date);

    let formattedDate = "";

    if (isISODate) {
      formattedDate = new Date(date).toISOString().split("T")[0];
    } else if (isDDMMYYYY) {
      const [day, month, year] = date.split("/");
      formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(
        2,
        "0"
      )}`;
    } else if (isYYYYMMDD) {
      formattedDate = date;
    }

    return formattedDate;
  };

  const handleSubmit = async (values) => {
    const updatedPersonalInfo = {
      ...(values?.firstName && { firstName: values.firstName }),
      ...{ firstNameArb: values.firstNameArb },
      ...(values?.lastName && { lastName: values.lastName }),
      ...{ lastNameArb: values.lastNameArb },
      ...(values?.phone && { phone: values.phone }),
      ...(values?.personalEmail && { personalEmail: values.personalEmail }),
      ...(values?.email && { email: values.email }),
      ...(values?.dobHijri && {
        dobHijri: convertDateForPayload(values.dobHijri),
      }),
      ...(values?.dobGregorian && { dobGregorian: values.dobGregorian }),
      ...(values?.religion?.value && { religion: values.religion.value }),
      ...(values?.maritalStatus && {
        maritalStatus: values.maritalStatus,
      }),
      ...(values?.gender && { gender: values.gender }),
      ...(values?.citizen && { citizen: values.citizen }),

      ...(values?.citizen === "false" &&
        values?.nationality?.value && {
          nationality: values.nationality.value,
        }),
      ...(values?.citizen === "true" &&
        values?.nationalId && {
          nationalId: values.nationalId,
        }),
    };
    setLoading(true);
    try {
      const response = await updatePersonalInfo(
        updatedPersonalInfo,
        personalInfo?.publicId,
        personalInfo?.concurrencyStamp
      );

      if (response?.data) {
        setIsEditing(false);
        toast("Personal Data Updated Successfully!", {
          style: { zIndex: 9999 },
        });
        getEmployeeRegistrationData(empPublicId);
        handleGetTableData();
      } else {
        notifyWarn("Failed to update designation");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "An unexpected error occurred",
        {
          style: { zIndex: 9999 },
        }
      );
      console.error("Error creating designation", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true); // Enable edit mode
  };

  return (
    <>
      <div className="tab-section">
        <div className="tab-header-container">
          <h3 className="tab-heading">{t("Personal")}</h3>
          {!isEditing && ( // Show edit button only when not in edit mode
            <div
              className="tab-header-edit-container"
              onClick={handleEditClick}
            >
              <img src={Edit} alt="edit icon" />
              <span className="edit-txt">{t("Edit")}</span>
            </div>
          )}
        </div>
        <Formik
          enableReinitialize
          initialValues={{
            firstName: personalInfo?.firstName || "",
            lastName: personalInfo?.lastName || "",
            firstNameArb: personalInfo?.firstNameArb || "",
            lastNameArb: personalInfo?.lastNameArb || "",
            phone: personalInfo?.phone || "",
            personalEmail: personalInfo?.personalEmail || "", // Already exists
            email: personalInfo?.email || "",
            country: personalInfo?.country || "",
            dobGregorian: personalInfo?.dobGregorian || "",
            dobHijri: personalInfo?.dobHijri || "",
            religion: personalInfo?.religionInfo
              ? {
                  label: personalInfo.religionInfo.name,
                  value: personalInfo.religionInfo.publicId,
                }
              : "",
            maritalStatus: personalInfo?.maritalStatus || "",
            gender: personalInfo?.gender || "",
            citizen: personalInfo?.citizen || "",
            nationality: personalInfo?.nationalityInfo
              ? {
                  label: personalInfo.nationalityInfo.name,
                  value: personalInfo.nationalityInfo.publicId,
                }
              : personalInfo?.nationality || null,
            nationalId: personalInfo?.nationalId || "",
          }}
          validationSchema={getValidationSchema()} // Your validation schema
          onSubmit={(values) => handleSubmit(values)}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            handleBlur,
          }) => (
            <Form>
              <div className="add-body-content-grid">
                {/* First Name */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="firstName">
                        {t("Employee First Name (EN)")}
                      </label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <input
                        id="firstName"
                        name="firstName"
                        className="add-input-update"
                        placeholder={t("First Name (EN)")}
                        value={values?.firstName}
                        onChange={(e) =>
                          setFieldValue("firstName", e.target.value)
                        }
                      />
                    ) : (
                      <div className="display-value">{values.firstName}</div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="firstName"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="firstNameArb">
                        {t("Employee First Name (AR)")}
                      </label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <input
                        id="firstNameArb"
                        name="firstNameArb"
                        className="add-input-update"
                        placeholder={t("First Name (EN)")}
                        value={values?.firstNameArb}
                        onChange={(e) =>
                          setFieldValue("firstNameArb", e.target.value)
                        }
                      />
                    ) : (
                      <div className="display-value">{values.firstNameArb}</div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="firstNameArb"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Last Name */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="lastName">
                        {t("Employee Last Name (EN)")}
                      </label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <input
                        id="lastName"
                        name="lastName"
                        className="add-input-update"
                        placeholder={"Last Name (EN)"}
                        value={values?.lastName}
                        onChange={(e) =>
                          setFieldValue("lastName", e.target.value)
                        }
                      />
                    ) : (
                      <div className="display-value">{values.lastName}</div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="lastName"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="lastNameArb">
                        {t("Employee Last Name (AR)")}
                      </label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <input
                        id="lastNameArb"
                        name="lastNameArb"
                        className="add-input-update"
                        placeholder={"Last Name (AR)"}
                        value={values?.lastNameArb}
                        onChange={(e) =>
                          setFieldValue("lastNameArb", e.target.value)
                        }
                      />
                    ) : (
                      <div className="display-value">{values.lastNameArb}</div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="lastNameArb"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Personal Email */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="personalEmail">
                        {t("Personal Email")}
                      </label>
                      {isEditing && !currentEmployeeEditing && (
                        <span className="add-req-mark">*</span>
                      )}
                    </div>
                    {currentEmployeeEditing && isEditing ? (
                      <div className="add-input-value">
                        <span>{values?.personalEmail || "-"}</span>
                        <span className="add-input-badge">{noEditingMsg}</span>
                      </div>
                    ) : isEditing ? (
                      <input
                        id="personalEmail"
                        name="personalEmail"
                        className="add-input-update"
                        placeholder={t("Enter Personal Email")}
                        value={values?.personalEmail}
                        onChange={(e) =>
                          setFieldValue("personalEmail", e.target.value)
                        }
                        onBlur={handleBlur}
                      />
                    ) : (
                      <div className="display-value">
                        {values?.personalEmail || "-"}
                      </div>
                    )}
                  </div>
                  {isEditing && !currentEmployeeEditing && (
                    <CustomErrorMessage
                      name="personalEmail"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Email */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="email">
                        {t("Email")}
                      </label>
                      {isEditing && !currentEmployeeEditing && (
                        <span className="add-req-mark">*</span>
                      )}
                    </div>
                    {currentEmployeeEditing && isEditing ? (
                      <div className="add-input-value">
                        <span>{values?.email || "-"}</span>
                        <span className="add-input-badge">{noEditingMsg}</span>
                      </div>
                    ) : isEditing ? (
                      <input
                        id="email"
                        name="email"
                        className="add-input-update"
                        placeholder={t("Enter Email")}
                        value={values?.email}
                        onChange={(e) => setFieldValue("email", e.target.value)}
                        onBlur={handleBlur}
                      />
                    ) : (
                      <div className="display-value">
                        {values?.email || "-"}
                      </div>
                    )}
                  </div>
                  {isEditing && !currentEmployeeEditing && (
                    <CustomErrorMessage
                      name="email"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Phone */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="phone">
                        {t("Contact Number")}
                      </label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <input
                        id="phone"
                        name="phone"
                        className="add-input-update"
                        placeholder={t("Enter Saudi mobile number")}
                        value={values?.phone}
                        onChange={(e) => setFieldValue("phone", e.target.value)}
                      />
                    ) : (
                      <div className="display-value">{values.phone}</div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="phone"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Date of Birth (Hijri) */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="dobHijri">
                        {t("Date Of Birth (Hijri)")}
                      </label>
                    </div>
                    {isEditing ? (
                      <div className="add-input-wrapper">
                        <HijriDatePicker
                          value={
                            values.dobHijri
                              ? convertAndFormatHijriDate(values.dobHijri)
                              : ""
                          } // Pass the Formik value
                          onChange={(date) => {
                            convertHijriToGregorian(date, setFieldValue);
                            setFieldValue("dobHijri", date); // Update Formik value
                          }}
                        />
                      </div>
                    ) : (
                      <div className="display-value">
                        {convertAndFormatHijriDate(values.dobHijri)}
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="dobHijri"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Date of Birth (Gregorian) */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="dobGregorian">
                        {t("Date Of Birth (Gregorian)")}
                      </label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <div className="add-input-wrapper">
                        <input
                          type="date"
                          id="dobGregorian"
                          name="dobGregorian"
                          className={`add-wrap-input ${
                            !values?.dobGregorian ||
                            values.dobGregorian === "DD-MM-YYYY"
                              ? "empty"
                              : ""
                          }`}
                          placeholder="DD/MM/YYYY"
                          value={
                            values?.dobGregorian
                              ? formatDateToYYYYMMDD(values.dobGregorian)
                              : ""
                          }
                          onChange={(e) => {
                            convertGregorianToHijri(
                              e.target.value,
                              setFieldValue
                            );
                            setFieldValue("dobGregorian", e.target.value);
                          }}
                        />
                        <img
                          src={dateIcon}
                          alt="date icon"
                          className="add-date-input-in"
                        />
                      </div>
                    ) : (
                      <div className="display-value">
                        {formatDateToYYYYMMDD(values.dobGregorian)}
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="dobGregorian"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Religion */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="religion">
                        {t("Religion")}
                      </label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <AsyncSelect
                        id="religion"
                        name="religion"
                        value={values.religion}
                        onChange={(selectedOption) =>
                          setFieldValue("religion", selectedOption)
                        }
                        loadOptions={loadReligionOptions}
                        defaultOptions={religionOptions}
                        styles={asyncSelectStyles}
                        cacheOptions
                        placeholder={t("Select religion")}
                      />
                    ) : (
                      <div className="display-value">
                        {values.religion?.label || ""}
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="religion"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Marital Status */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label">{t("Marital Status")}</label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <div className="add-radio-input-container">
                        <div className="add-radio-container">
                          <input
                            id="married"
                            type="radio"
                            name="maritalStatus"
                            className="add-radio-input"
                            value="Married"
                            onChange={(e) =>
                              setFieldValue("maritalStatus", e.target.value)
                            }
                            checked={values?.maritalStatus === "Married"}
                          />
                          <label htmlFor="married" className="add-label">
                            {t("Married")}
                          </label>
                        </div>
                        <div className="add-radio-container">
                          <input
                            id="single"
                            type="radio"
                            name="maritalStatus"
                            className="add-radio-input"
                            value="Single"
                            onChange={(e) =>
                              setFieldValue("maritalStatus", e.target.value)
                            }
                            checked={values?.maritalStatus === "Single"}
                          />
                          <label htmlFor="single" className="add-label">
                            {t("Single")}
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="display-value">
                        {values.maritalStatus}
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="maritalStatus"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Gender */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label">{t("Gender")}</label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <div className="add-radio-input-container">
                        <div className="add-radio-container">
                          <input
                            id="Male"
                            type="radio"
                            name="gender"
                            className="add-radio-input"
                            value="Male"
                            onChange={(e) =>
                              setFieldValue("gender", e.target.value)
                            }
                            checked={values?.gender === "Male"}
                          />
                          <label htmlFor="Male" className="add-label">
                            {t("Male")}
                          </label>
                        </div>
                        <div className="add-radio-container">
                          <input
                            id="Female"
                            type="radio"
                            name="gender"
                            className="add-radio-input"
                            value="Female"
                            onChange={(e) =>
                              setFieldValue("gender", e.target.value)
                            }
                            checked={values?.gender === "Female"}
                          />
                          <label htmlFor="Female" className="add-label">
                            {t("Female")}
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="display-value">{values.gender}</div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="gender"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* Citizen */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label">{t("Citizen")}</label>
                      {isEditing && <span className="add-req-mark">*</span>}
                    </div>
                    {isEditing ? (
                      <div className="add-radio-input-container">
                        <div className="add-radio-container">
                          <input
                            id="yes"
                            type="radio"
                            name="citizen"
                            className="add-radio-input"
                            value="true"
                            onChange={(e) =>
                              setFieldValue("citizen", e.target.value)
                            }
                            checked={values?.citizen === "true"}
                          />
                          <label htmlFor="yes" className="add-label">
                            {t("Yes")}
                          </label>
                        </div>
                        <div className="add-radio-container">
                          <input
                            id="no"
                            type="radio"
                            name="citizen"
                            className="add-radio-input"
                            value="false"
                            onChange={(e) =>
                              setFieldValue("citizen", e.target.value)
                            }
                            checked={values?.citizen === "false"}
                          />
                          <label htmlFor="no" className="add-label">
                            {t("No")}
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="display-value">
                        {values.citizen === "true" ? "Yes" : "No"}
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <CustomErrorMessage
                      name="citizen"
                      errors={errors}
                      touched={touched}
                    />
                  )}
                </div>

                {/* National ID or Nationality */}
                {values?.citizen === "true" ? (
                  <div className="form-group">
                    <div className="add-body-input-section">
                      <div className="add-label-container">
                        <label className="add-label" htmlFor="nationalId">
                          {t("National ID")}
                        </label>
                        {isEditing && <span className="add-req-mark">*</span>}
                      </div>
                      {isEditing ? (
                        <input
                          id="nationalId"
                          name="nationalId"
                          className="add-input-update"
                          placeholder={t("Enter National ID")}
                          value={values?.nationalId}
                          onChange={(e) =>
                            setFieldValue("nationalId", e.target.value)
                          }
                        />
                      ) : (
                        <div className="display-value">{values.nationalId}</div>
                      )}
                    </div>
                    {isEditing && (
                      <CustomErrorMessage
                        name="nationalId"
                        errors={errors}
                        touched={touched}
                      />
                    )}
                  </div>
                ) : (
                  <div className="form-group">
                    <div className="add-body-input-section">
                      <div className="add-label-container">
                        <label className="add-label" htmlFor="nationality">
                          {t("Nationality")}
                        </label>
                        {isEditing && <span className="add-req-mark">*</span>}
                      </div>
                      {isEditing ? (
                        <AsyncSelect
                          id="nationality"
                          name="nationality"
                          value={values.nationality}
                          onChange={(selectedOption) =>
                            setFieldValue("nationality", selectedOption)
                          }
                          loadOptions={loadOptions}
                          defaultOptions={countryOptions}
                          styles={asyncSelectStyles}
                          cacheOptions
                          placeholder={t("Select nationality")}
                        />
                      ) : (
                        <div className="display-value">
                          {values.nationality?.label || ""}
                        </div>
                      )}
                    </div>
                    {isEditing && (
                      <CustomErrorMessage
                        name="nationality"
                        errors={errors}
                        touched={touched}
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Submit button (only visible in edit mode) */}
              {isEditing && (
                <div className="add-footer">
                  <span></span>
                  <div className="add-footer-right">
                    <button
                      type="button"
                      className="add-footer-cancel-btn"
                      onClick={() => setIsEditing(false)}
                    >
                      {t("Cancel")}
                    </button>
                    <button
                      type="submit"
                      className="add-footer-submit-btn"
                      disabled={loading}
                    >
                      {loading ? <ButtonLoading /> : t("Submit")}
                    </button>
                  </div>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default PersonalNew;
